import { useMemo } from 'react';

import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import {
	type LinkedEntityGranularity,
	dynamicExperimentsSchema,
} from '../../schemas/dynamic-config';
import {
	type MultiLayerBackendExperiment,
	type ServedExperiment,
	type SideBySideExperiment,
} from '../../types';
import { DEFAULT_STATSIG_EXPERIMENT_CONFIG } from '../constants';
import { getBackendExperimentConfig } from './backend';
import type { GetLayerValuesFn } from './dynamic/types';
import { getFrontendExperiment } from './frontend';

/**
 * This function fetches the experiment configuration within a given search experience layer
 * and will return the data for either the frontend experiment(s) or the backend experiment.
 *
 * If frontend experiment(s) exist in the layer,
 * * The frontend experiment exposure IS fired
 * * The backend experiment exposure IS NOT fired
 * * Backend experiment data IS NOT passed in requests
 *
 * If there is no frontend experiment(s) in the layer,
 * * The frontend experiment exposure IS NOT fired
 * * The backend experiment exposure IS fired
 * * Backend experiment data IS passed in requests
 */
export const getMutuallyExclusiveExperiment: (
	layerName?: string,
	onErrorFn?: (error: any) => any,
	shouldNotGetBackendExperiment?: boolean,
	getLayerValuesFn?: GetLayerValuesFn,
) =>
	| {
			frontendExperiment: undefined;
			backendExperiment: MultiLayerBackendExperiment | undefined;
	  }
	| {
			frontendExperiment: ServedExperiment;
			backendExperiment: {
				readonly experimentId: 'default';
				readonly shadowExperimentId: 'default';
				readonly controlId: 'default';
				readonly abTestId: 'default';
			};
	  }
	| {
			frontendExperiment: undefined;
			backendExperiment: {
				readonly experimentId: 'default';
				readonly shadowExperimentId: 'default';
				readonly controlId: 'default';
				readonly abTestId: 'default';
			};
	  } = (
	layerName?: string,
	onErrorFn?: (error: any) => any,
	shouldNotGetBackendExperiment: boolean = false,
	getLayerValuesFn?: GetLayerValuesFn,
) => {
	try {
		if (!layerName) {
			return {
				frontendExperiment: undefined,
				backendExperiment: getBackendExperimentConfig(
					onErrorFn,
					shouldNotGetBackendExperiment,
					getLayerValuesFn,
				),
			};
		}

		const layer = FeatureGates.getLayer(layerName, { fireLayerExposure: false });
		const frontendExperimentName = layer.get('frontendExperimentName', '');

		if (frontendExperimentName) {
			return {
				frontendExperiment: getFrontendExperiment({
					abTestId: frontendExperimentName,
					onError: (error) => onErrorFn?.(error),
				}),
				backendExperiment: DEFAULT_STATSIG_EXPERIMENT_CONFIG,
			};
		}

		return {
			frontendExperiment: undefined,
			backendExperiment: getBackendExperimentConfig(
				onErrorFn,
				shouldNotGetBackendExperiment,
				getLayerValuesFn,
			),
		};
	} catch (error) {
		onErrorFn?.(error);
	}

	return {
		frontendExperiment: undefined,
		backendExperiment: DEFAULT_STATSIG_EXPERIMENT_CONFIG,
	};
};

/**
 * Handles any backend experiment overrides for Rovo vs Rovo in SBS
 * This function only allows overrides for backend experiments; gates, layers
 * will not work currently.
 *
 * If an override is passed in for an experiment for particular layer, L1, L2 etc,
 * this will override that specific layer's experiment only, leaving all other experiments
 * as is. E.g. if an experiment for L1 is passed in only search_platform_layer_l1 will be overriden
 *
 *  Falls back to a standard statsig call if:
 * - search-experiment-overrides-backend is off
 * - errors
 * - no overrides provided
 * - overrides of non backend, experiment type
 * - experiments with no testId
 */
export const getBackendExperimentsWithOverrides: (
	overrides: SideBySideExperiment[] | undefined,
	layerName?: string,
	onErrorFn?: (error: any) => any,
	shouldNotGetBackendExperiment?: boolean,
	getLayerValuesFn?: GetLayerValuesFn,
) => {
	backendExperiment:
		| MultiLayerBackendExperiment
		| {
				readonly experimentId: 'default';
				readonly shadowExperimentId: 'default';
				readonly controlId: 'default';
				readonly abTestId: 'default';
		  }
		| undefined;
	frontendExperiment: ServedExperiment | undefined;
} = (
	overrides: SideBySideExperiment[] | undefined,
	layerName?: string,
	onErrorFn?: (error: any) => any,
	shouldNotGetBackendExperiment: boolean = false,
	getLayerValuesFn?: GetLayerValuesFn,
) => {
	// We should only evaluate the backend experiment here if we are using search experiment overrides functionality
	// backend experiments outside of rovo v rovo are evaluated at request time instead
	const shouldUseSearchExperimentOverrides = fg('search-experiment-overrides-backend');
	const shouldNotGetBackendExperimentsForOverrides = shouldUseSearchExperimentOverrides
		? false
		: shouldNotGetBackendExperiment;

	// If the feature gate is enabled or no overrides are provided, return the default experiment.
	if (!shouldUseSearchExperimentOverrides || !overrides || overrides.length === 0) {
		return getMutuallyExclusiveExperiment(
			layerName,
			onErrorFn,
			shouldNotGetBackendExperimentsForOverrides,
			getLayerValuesFn,
		);
	}

	// Filter for backend experiment from the overrides. only allowing them for now
	const backendOverrideExperiments = overrides.filter(
		(override) => override.location === 'backend' && override.type === 'experiment',
	);

	if (!backendOverrideExperiments.length) {
		return getMutuallyExclusiveExperiment(
			layerName,
			onErrorFn,
			shouldNotGetBackendExperimentsForOverrides,
			getLayerValuesFn,
		);
	}

	// Get the base user's experiments from layers.
	const { backendExperiment, frontendExperiment } = getMutuallyExclusiveExperiment(
		layerName,
		onErrorFn,
		shouldNotGetBackendExperimentsForOverrides,
		getLayerValuesFn,
	);

	if (backendExperiment && 'experimentLayers' in backendExperiment) {
		backendExperiment.experimentLayers = backendExperiment?.experimentLayers.map((layer) => {
			const layerToOverride = backendOverrideExperiments.find((exp) => {
				return (
					exp.layer && layer.name?.includes(exp.layer === 'qr' ? 'query_expansion' : exp.layer)
				);
			});

			if (!layerToOverride) {
				const { testId: _, ...rest } = layer;
				return rest;
			}
			const statsigOverrideExperiment = FeatureGates.getExperiment(layerToOverride?.name);
			// handle case where experiment didnt add testId - for older experiments should be on new ones
			if (!statsigOverrideExperiment.get<string | null>('testId', null)) {
				const { testId: _, ...rest } = layer;
				return rest;
			}
			return {
				name: layer.name,
				layerId: statsigOverrideExperiment.get<string | null>('testId', null),
				shadowId: statsigOverrideExperiment.get<string | null>('shadowId', null),
				// The below values might not be sent to the backend, but we can still track them in the frontend.
				controlId: statsigOverrideExperiment.get<string | null>('controlId', null),
				abTestId: statsigOverrideExperiment.get<string | null>('abTestId', null),
				armId: statsigOverrideExperiment.get<string | null>('armId', null),
			};
		});
	}
	return { backendExperiment, frontendExperiment };
};

type ExperimentConfigParams = {
	experimentLayers?: string[];
	sideBySideExperiment?: SideBySideExperiment[];
	statsigLayer?: string;
	onError: (error: Error) => void;
	/** Optional layer value resolver — defaults to the browser FeatureGates implementation.
	 *  SSR can inject its own adapter to resolve layer values from the node client. */
	getLayerValuesFn?: GetLayerValuesFn;
};

/**
 * Pure function to generate the experiment configuration, based on the experiment layers and the statsig layer.
 * This is the shared core logic used by both useExperimentConfig (hook) and direct callers.
 */
export const getExperimentConfig = ({
	experimentLayers,
	sideBySideExperiment,
	statsigLayer,
	onError,
	getLayerValuesFn,
}: ExperimentConfigParams): {
	backendExperiment:
		| MultiLayerBackendExperiment
		| {
				readonly experimentId: 'default';
				readonly shadowExperimentId: 'default';
				readonly controlId: 'default';
				readonly abTestId: 'default';
		  }
		| undefined;
	frontendExperiments: {
		[x: string]: ServedExperiment;
	};
	dynamicExperiments:
		| {
				statsigLayerId: string;
				layerType?: string | undefined;
				entity?: string | undefined;
				subEntity?: string | undefined;
				hello?: boolean | undefined;
				isJiraIssueIndexed?: boolean | undefined;
				linkedEntityGranularity?: LinkedEntityGranularity | undefined;
				confluenceTier?: string[] | undefined;
				productDataRegions?: Record<string, string[]> | undefined;
				experiences?:
					| (
							| 'search-page'
							| 'quick-find'
							| 'search-page-space-filter'
							| 'quick-search'
							| 'advanced-search'
							| 'sain'
					  )[]
					| undefined;
				tenantIds?: string[] | undefined;
				targetingGateId?: string | undefined;
				providerId?: string | undefined;
				connectorSources?: string[] | undefined;
				integrationAri?: string | undefined;
		  }[]
		| undefined;
} => {
	const { frontendExperiment, backendExperiment } = getBackendExperimentsWithOverrides(
		sideBySideExperiment,
		statsigLayer,
		onError,
		true,
		getLayerValuesFn,
	);

	// This experiment can be alternated between mutually exclusive or not.
	const multiSiteSearchInRovoExperiment = getFrontendExperiment({
		abTestId: 'multi_site_search_in_rovo',
		onError,
	});

	return {
		backendExperiment,
		frontendExperiments: {
			// the frontend experiment that's mutually exclusive to other BE and FE experiments
			...(frontendExperiment?.abTestId && { [frontendExperiment.abTestId]: frontendExperiment }),
			// add other frontend experiments that can be run alongside backend experiments here
			// NOTE: these aren't mutually exclusive to other BE or FE experiments
			[multiSiteSearchInRovoExperiment.abTestId]: multiSiteSearchInRovoExperiment,
		},
		dynamicExperiments: dynamicExperimentsSchema.safeParse(experimentLayers ?? []).data,
	};
};

/**
 * This hook is used to generate the experiment configuration, based on the experiment layers and the statsig layer.
 * Thin hook wrapper around getExperimentConfig — delegates all logic to the pure function.
 */
export const useExperimentConfig = ({
	experimentLayers,
	sideBySideExperiment,
	statsigLayer,
	onError,
}: ExperimentConfigParams): {
	backendExperiment:
		| MultiLayerBackendExperiment
		| {
				readonly experimentId: 'default';
				readonly shadowExperimentId: 'default';
				readonly controlId: 'default';
				readonly abTestId: 'default';
		  }
		| undefined;
	frontendExperiments: {
		[x: string]: ServedExperiment;
	};
	dynamicExperiments:
		| {
				statsigLayerId: string;
				layerType?: string | undefined;
				entity?: string | undefined;
				subEntity?: string | undefined;
				hello?: boolean | undefined;
				isJiraIssueIndexed?: boolean | undefined;
				linkedEntityGranularity?: LinkedEntityGranularity | undefined;
				confluenceTier?: string[] | undefined;
				productDataRegions?: Record<string, string[]> | undefined;
				experiences?:
					| (
							| 'search-page'
							| 'quick-find'
							| 'search-page-space-filter'
							| 'quick-search'
							| 'advanced-search'
							| 'sain'
					  )[]
					| undefined;
				tenantIds?: string[] | undefined;
				targetingGateId?: string | undefined;
				providerId?: string | undefined;
				connectorSources?: string[] | undefined;
				integrationAri?: string | undefined;
		  }[]
		| undefined;
} => {
	return useMemo(
		() =>
			getExperimentConfig({
				experimentLayers,
				sideBySideExperiment,
				statsigLayer,
				onError,
			}),
		[experimentLayers, sideBySideExperiment, onError, statsigLayer],
	);
};
